import {AgEltBoxSelection, IBoxStopSelection, IEltBoxSelection, IWedEditorBoxSel} from "back/edit/wed/features/boxSel";
import {BoxCommentWedlet} from "back/edit/wed/wedlets/box/box";
import {EWedletEditMode, IElementWedlet, IWedletActionCtx, WEDLET} from "back/edit/wed/wedlets/wedlet";
import {IReg, REG} from "lib/commons/registry";
import {DOM, JSX} from "lib/commons/xml/dom";
import {DOMSH} from "lib/commons/xml/domsh";
import {IJmlNode, IJmlSubSet} from "lib/commons/xml/jml";
import {DocHolder, IDocHolderSync} from "lib/edit/docHolder";
import {WedEditorFrag} from "back/edit/wed/wedEditorBox";
import {IWedEditorCommonBar} from "back/edit/wed/features/commonBar";
import {ACTION, Action, ActionHackCtx, IAction} from "lib/commons/actions";
import {IXAddrRange, XA} from "lib/commons/xml/xAddr";
import {LANG} from "lib/commons/lang";
import {OSkImportDatas, OSkPasteContext, OSkPasteSearchContext} from "lib/edit/schema/schemaMeta";
import {POPUP} from "back/commons/widgets/popups";

/**
 * Exclusion de fragments mis en commentaires.
 */
export class BoxScExclude extends HTMLElement implements IElementWedlet, IEltBoxSelection {

	wedlet: BoxCommentWedlet;

	btn: HTMLElement;

	cmtDoc: IDocHolderSync;
	subEditor: WedEditorFrag | null;

	configWedletElt(tpl: Element, wedlet: BoxCommentWedlet) {
		this.setAttribute("sc-comments", "no");
		this.title = "Contenu exclu"
		const parentWedMgr = wedlet.wedMgr;
		this.wedlet = wedlet;
		this.btn = <div id="btn" onclick={this.onClick}/>;
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		WEDLET.installSkins(tpl, sr, wedlet, this.localName);
		sr.append(this.btn);
		if (wedlet.cmtHouse != null) {
			wedlet.cmtHouse.then(async (cmtH) => {
				this.cmtDoc = new DocHolder(cmtH);
				this.subEditor = new WedEditorFrag().initialize({reg: parentWedMgr.reg, commonBar: (parentWedMgr.wedEditor as IWedEditorCommonBar).commonBar} as IWedEditorCommonBar);
				this.subEditor.wedModel = parentWedMgr.wedModel;
				const exclModel = wedlet.wedParent.model.buildModelForFragment(wedlet.wedParent);
				sr.appendChild(this.subEditor);
				this.subEditor.onkeydown = this.onKeyDownSubEditor;
				(this.subEditor as IBoxStopSelection).stopBoxSelection = true;
				this.subEditor.addEventListener("focusout", (ev: FocusEvent) => {
					if (ev.relatedTarget === this) {
						//On redonne le focus au container: pas d'event focus dans ce cas.
						parentWedMgr.listeners.emit("getFocus", parentWedMgr, this);
					}
				}, true);
				this.subEditor.scrollContainer.addEventListener("scroll", function (ev) {
					//Cas d'un affichage en absolu qui déborde : BoxPane...
					this.style.minHeight = Math.ceil(this.scrollHeight) + 'px';
				}, {passive: true});
				await this.subEditor.initFromDocHolder(this.cmtDoc, {
					noAnnots: true,
					readOnlyCauses: ["excludeCtx"],
					xaRoot: [0],
					rootModel: exclModel,
					overrideReg: (reg: IReg<any>) => {
						reg.addToList("actions:wed:global", "include", 1, new IncludeInSubEditor(includeAction, {
							wedMgr: parentWedMgr,
							focusedElt: this,
							wedlet
						}), -999);
					}
				});
				if (this.collapsed) this._doCollapse(true);
			});
		}
		this.ondblclick = this.toggle;
		this.onkeydown = this.onKeyDown;
	}

	get collapsed(): boolean {return this.hasAttribute('collapsed')}

	set collapsed(state: boolean) {
		if (DOM.setAttrBool(this, 'collapsed', state)) {
			this._doCollapse(state)
			//Aucune modif du contenu redraw du wed : "wed-resized" permet notifier à la boxErrBar par exemple de se recalculer.
			this.dispatchEvent(new CustomEvent("wed-resized", {bubbles: true, composed: true}));
		}
	}

	toggle(ev?: Event) {
		ev?.stopImmediatePropagation();
		ev?.preventDefault();
		this.collapsed = !this.collapsed
	}

	protected _doCollapse(st: boolean) {
		if (this.subEditor) {
			this.subEditor.hidden = st;
			if (!st) this.subEditor.scrollContainer.style.minHeight = null; //reset d'une hauteur forcée précédemment (via event scroll)
		}
	}

	setEditMode(mode: EWedletEditMode): void {
		//toujours readonly
	}

	refreshBindValue(val: IJmlNode | string, children?: IJmlSubSet): void {
	}

	onClick(this: HTMLElement) {
		DOMSH.findHost<BoxScExclude>(this).toggle();
	}

	onKeyDown(ev: KeyboardEvent) {
		if (ev.target !== this) return;
		if (ev.key === 'ArrowRight' || ev.key === 'Tab') {
			if (!ACTION.isAccelPressed(ev) && this.collapsed) this.collapsed = false;
		} else if (LANG.in(ev.key, 'Enter', ' ') && !ACTION.isAnyControlPressed(ev)) {
			this.toggle(ev);
		}
	}

	onKeyDownSubEditor(this: WedEditorFrag, ev: KeyboardEvent) {
		if (ev.key === 'Backspace' || ev.key === 'Delete') {
			ev.stopImmediatePropagation();
			ev.preventDefault();
		}
	}
}

AgEltBoxSelection(BoxScExclude, {selMode: "box", actionsLists: ['actions:wed:box-scexclude', 'actions:wed:box']});

REG.reg.registerSkin('box-scexclude', 1, /* language=CSS */ `
	:host {
		background-color: var(--edit-exclude-bgcolor, #eaeaea);
		background-image: linear-gradient(rgba(0, 0, 0, .03), rgba(0, 0, 0, .03));
		display: flex;
		min-height: 0;
		min-width: 0;
		flex-direction: column;
		margin: .5em;
		box-shadow: .1em .1em .5em .1em var(--edit-exclude-shadow-color, #b6b6b6);
		clear: both;
		padding: 0 .5em .5em .5em;
	}

	:host(:focus) {
		outline: var(--edit-box-focus);
	}

	#btn {
		align-self: start;
		min-width: 1em;
		min-height: 1em;
		background: no-repeat top / contain url(/@skin@/edit/wed/box/collapse.svg);
		cursor: pointer;
	}

	:host([collapsed]) {
		background-image: repeating-linear-gradient(-30deg, rgba(0, 0, 0, .03), rgba(0, 0, 0, .03) 5px, rgba(255, 255, 255, .03) 5px, rgba(255, 255, 255, .03) 10px);
	}

	:host([collapsed]) > #btn {
		background: no-repeat top / contain url(/@skin@/edit/wed/box/uncollapse.svg);
	}

	box-editor-frag {
		flex: 1;
		opacity: .6;
		font-size: .8em;
		background-color: var(--edit-exclude-bgcolor, #eaeaea);
		background-image: repeating-linear-gradient(-30deg, rgba(0, 0, 0, .03), rgba(0, 0, 0, .03) 5px, rgba(255, 255, 255, .03) 5px, rgba(255, 255, 255, .03) 10px);
		border: 1px solid var(--edit-exclude-bgcolor, #eaeaea);
	}
`);

window.customElements.define("box-scexclude", BoxScExclude);

const includeAction = new Action<IWedletActionCtx>("wedBoxInclude")
	.setLabel("Réinclure")
	.setIcon("/@skin@/edit/wed/box/includeTag.svg")
	.setVisible((ctx) => {
		return (ctx.wedMgr.wedEditor as IWedEditorBoxSel).boxSelMgr.isSingleSel();
	})
	.setEnabled((ctx) => {
		return WEDLET.resolveEditMode(ctx.wedlet.wedParent) === EWedletEditMode.write
	})
	.setExecute(async (ctx) => {
		//console.log("ctx:::::::", ctx);
		const box = ctx.focusedElt as BoxScExclude;
		const excludedRoot = box.cmtDoc.getDocument().documentElement;
		const toInsertFrag = box.cmtDoc.house.exportFragment({start: XA.fromNode(excludedRoot.firstChild), end: XA.incrAtDepth(XA.fromNode(excludedRoot.lastChild), -1, 1)});
		const doc = ctx.wedMgr.docHolder;

		//On réinsert les éléments 1 par 1 car le schéma pourrait imposer de fragmenter la séquence
		//si des éléments optionnels intermédiaires étaient absents au moment de l'exclusion.


		async function doReinsert(node: Node, sel: IXAddrRange, addRemExclude?: boolean): Promise<boolean> {
			const impCtx: OSkPasteContext = {sel};
			const cache: OSkImportDatas = {};
			const importers = await doc.house.schemaDom.tryPasteNodes(impCtx, node, cache);
			if (!box.isConnected) return; //racecond
			if (importers?.length > 0) {
				//TODO user select importer si plusieurs ?
				const importer = importers[0];
				if (importer.malus === 0) {
					const batch = doc.newBatch();
					//console.log("importer:::", importer, sel);
					importer.doImport(impCtx, batch);
					//if (addRemExclude) batch.needAdjustForNextAdds().deleteSequence(box.wedlet.wedAnchor, 1);
					batch.doBatch();
					return true;
				}
			}
			return false;
		}

		//Tentative de remplacement simple en une seule étape
		const toInsertRoot = toInsertFrag.ownerDocument.createElementNS(DOM.SCCORE_NS, DOM.SCFRAGMENT_TAG);
		toInsertRoot.appendChild(toInsertFrag);
		if (!await doReinsert(toInsertRoot, XA.newRangeAround(ctx.wedlet.wedAnchor))) {
			//Echec au remplacement simple du contenu exclu
			//Recherche d'un point d'insertion
			if (!box.isConnected) return; //racecond
			const impCtx: OSkPasteSearchContext = {xaParent: ctx.wedlet.wedParent.wedAnchor};
			const datas: OSkImportDatas = {originalDom: toInsertRoot}
			const importers = await doc.house.schemaDom.tryImport(impCtx, datas);
			if (!box.isConnected) return; //racecond
			if (importers?.length > 0) {
				const importer = importers[0];
				const impPos = importer.importPos;
				if (impPos.insertOffsetMin == null && impPos.replaceChildren?.length === 1) {
					//On a trouvé un point précis de remplacement
					const batch = doc.newBatch();
					importer.doImport({sel: XA.newRangeAround(XA.append(box.wedlet.wedParent.wedAnchor, impPos.replaceChildren[0] as number))}, batch);
					batch.needAdjustForNextAdds().deleteSequence(box.wedlet.wedAnchor, 1);
					batch.doBatch();
				} else if (!impPos.replaceChildren && impPos.insertOffsetMin === impPos.insertOffsetMax) {
					//On a trouvé un point précis d'insertion
					const batch = doc.newBatch();
					importer.doImport({sel: {start: XA.append(box.wedlet.wedParent.wedAnchor, impPos.insertOffsetMin)}}, batch);
					batch.needAdjustForNextAdds().deleteSequence(box.wedlet.wedAnchor, 1);
					batch.doBatch();
				} else {
					POPUP.showNotifInfo("Ce contenu exclu n'a pas pu être réintroduit simplement. Réintégrez-le par des actions de copier et coller.", box)
				}
			}
		}
	});

/** Action de réinclusion dans le subEditor */
class IncludeInSubEditor extends ActionHackCtx<IWedletActionCtx, IWedletActionCtx> {
	constructor(sub: IAction<IWedletActionCtx>, public ctx: IWedletActionCtx) {super(sub)}

	getGroup(ctx: IWedletActionCtx): string {return "UP"}

	wrapCtx(ctx: IWedletActionCtx): IWedletActionCtx {return this.ctx}
}

REG.reg.addToList("actions:wed:box-scexclude", "include", 1, includeAction, 10);

REG.reg.addToList("actions:wed:box-scexclude", "exclude", 1, null); //élimine l'action d'exclusion issue de la liste "actions:wed:global"